import React, {useCallback, useEffect, useMemo, useState} from 'react'
import Button from '@mui/material/Button'
import {Avatar, Box, CircularProgress, Container, TextField, Typography, Modal, Tabs, Tab} from '@mui/material'
import {DataGrid} from '@mui/x-data-grid'
import _ from 'lodash'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../redux/rootReducer'
import {createDeviceColumns} from './deviceColumns'
import {createPatientColumns} from './patientColumns'
import {createUserColumns} from './userColumns'
import {ModalPage} from '../../nav/ModalPage'
import {actions as searchActions} from '../../redux/modules/search'
import {actions as deviceActions} from '../../redux/modules/devices'
import {actions as patientActions} from '../../redux/modules/patients'
import {actions as userActions} from '../../redux/modules/users'
import Device, {DeviceStatus, DEVICE_STATUS_FILTERS} from 'models/Device'
import Patient from 'models/Patient'
import styles from './styles'
import AutoResetPasswordPage from 'pages/autoResetPassword'
import AssignmentDashboardTab from 'pages/assignmentDashboard'
import DeviceLookupPage from 'pages/deviceLookup'

type SearchTab = 'devices' | 'patients' | 'users'

type Props = {
  onClickAddDevice: (patientId: string) => void
  onClickEditDevice: (device: Device) => void
  onClickEditPatient: (patient: Patient) => void
}

const SearchPage: React.FC<Props> = props => {
  const dispatch = useDispatch()
  const {results, isSearching} = useSelector(({search}: RootState) => search)
  const user = useSelector(({auth}: RootState) => auth.user)
  const patientsLoading = useSelector(({patients}: RootState) => patients.loading)
  const users = useMemo(() => _.values(results.users), [results.users])
  const patients = useMemo(() => _.values(results.patients), [results.patients])
  const [deviceStatusFilter, setDeviceStatusFilter] = useState<DeviceStatus | 'all'>('all')
  const allDevices = useMemo(() => _.values(results.devices), [results.devices])
  const devices = useMemo(
    () =>
      allDevices.filter(
        device => deviceStatusFilter === 'all' || device.status === deviceStatusFilter,
      ),
    [allDevices, deviceStatusFilter],
  )
  const showNoDevicesForStatusFilter =
    deviceStatusFilter !== 'all' && _.isEmpty(devices) && !_.isEmpty(allDevices)
  const [query, setQuery] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const {onClickAddDevice, onClickEditDevice, onClickEditPatient} = props
  const [currentModalPage, setCurrentModalPage] = useState<ModalPage | undefined>(undefined)
  const showModal = !!currentModalPage
  const {autoResetPasswordResponse, error} = useSelector((state: RootState) => state.users)
  const [tab, setTab] = useState<SearchTab>('devices')

  useEffect(() => {
    if (error) {
      alert(error)
    } else if (autoResetPasswordResponse?.newPassword) {
      openModal(() => <AutoResetPasswordPage onClickCancel={closeModal} />)
    }
  }, [autoResetPasswordResponse, error])

  const onClickDeleteDevice = useCallback((id: string) => {
    dispatch(deviceActions.delete({id}))
  }, [dispatch])
  const onClickLookupDevice = useCallback((device: Device) => {
    // DeviceLookupPage's own mount effect fetches detail/timeline — dispatching them here too
    // would double every request on the wire.
    openModal(() => <DeviceLookupPage udid={device.udid} onClickCancel={closeModal} />)
  }, [])
  const onClickDeletePatient = useCallback((id: string) => {
    dispatch(patientActions.delete({id}))
  }, [dispatch])
  const onClickResetPatientData = useCallback((id: string) => {
    dispatch(patientActions.resetData({id}))
  }, [dispatch])

  const openModal = (page: ModalPage) => {
    setCurrentModalPage(page)
  }

  const closeModal = () => {
    setCurrentModalPage(undefined)
  }

  const onClickAutoResetPassword = useCallback((id: string) => {
    dispatch(userActions.autoResetPassword({userId: id}))
  }, [dispatch])

  const canDeleteUser = !!user?.admin

  const onClickDeleteUser = useCallback((id: string) => {
    dispatch(userActions.delete({id}))
  }, [dispatch])

  const deviceColumns = useMemo(
    () => createDeviceColumns(onClickEditDevice, onClickDeleteDevice, onClickLookupDevice),
    [onClickEditDevice, onClickDeleteDevice, onClickLookupDevice],
  )
  const canResetPatientData =
    !!user?.admin && (process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'development')
  const patientColumns = useMemo(
    () => createPatientColumns(
      onClickEditPatient,
      onClickAddDevice,
      onClickDeletePatient,
      onClickResetPatientData,
      {showResetPatientData: canResetPatientData, isLoading: patientsLoading},
    ),
    [onClickEditPatient, onClickAddDevice, onClickDeletePatient, onClickResetPatientData, canResetPatientData, patientsLoading],
  )
  const userColumns = useMemo(
    () => createUserColumns(onClickAutoResetPassword, onClickDeleteUser, canDeleteUser),
    [onClickAutoResetPassword, onClickDeleteUser, canDeleteUser],
  )

  const search = () => {
    setHasSearched(true)
    dispatch(searchActions.search({query: _.trim(query)}))
  }

  const hasSearchResults = !_.isEmpty(users) || !_.isEmpty(patients) || !_.isEmpty(allDevices)
  const showNoResults = hasSearched && !isSearching && !hasSearchResults

  return (
    <>
      <Modal
        sx={{padding: 5}}
        open={showModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <>{currentModalPage}</>
      </Modal>
      <Container component="main" sx={styles.root}>
        <Box textAlign="center">
          <Avatar sx={styles.logo} variant="square" src="logo.png" />
        </Box>
        <AssignmentDashboardTab />
        <Box textAlign="center">
          <TextField
            sx={styles.searchBar}
            margin="normal"
            required
            id="query"
            placeholder="Enter device, patient or user data"
            name="query"
            autoComplete="email"
            autoFocus
            onChange={e => setQuery(e.target.value)}
            onKeyDown={args => args.key === 'Enter' && !isSearching && search()}
          />
          <Button
            variant="contained"
            sx={styles.searchButton}
            onClick={search}
            disabled={isSearching}>
            Search
          </Button>
        </Box>
        {isSearching && (
          <Box sx={styles.searchingContainer}>
            <CircularProgress />
          </Box>
        )}
        {showNoResults && (
          <Typography variant="h6" sx={styles.noResultsLabel}>
            No results found
          </Typography>
        )}
        {hasSearched && (
          <Tabs value={tab} onChange={(_e, value: SearchTab) => setTab(value)} sx={styles.tabBar}>
            <Tab label={`Devices (${allDevices.length})`} value="devices" />
            <Tab label={`Patients (${patients.length})`} value="patients" />
            <Tab label={`Users (${users.length})`} value="users" />
          </Tabs>
        )}
        {hasSearched && tab === 'devices' && (
          <Box sx={{display: 'flex', gap: 1, marginBottom: 2}}>
            {DEVICE_STATUS_FILTERS.map(({label, value}) => (
              <Button
                key={value}
                size="small"
                color="primary"
                variant={deviceStatusFilter === value ? 'contained' : 'outlined'}
                onClick={() => setDeviceStatusFilter(value)}>
                {label}
              </Button>
            ))}
          </Box>
        )}
        {tab === 'devices' && showNoDevicesForStatusFilter && (
          <Typography sx={styles.noResultsLabel}>
            No {deviceStatusFilter} devices found
          </Typography>
        )}
        {tab === 'devices' && !_.isEmpty(devices) && (
          <DataGrid
            rows={devices}
            columns={deviceColumns}
            autoHeight
            initialState={{pagination: {pageSize: 25}}}
            rowsPerPageOptions={[25, 50]}
            experimentalFeatures={{newEditingApi: true}}
          />
        )}
        {tab === 'patients' && !_.isEmpty(patients) && (
          <DataGrid
            rows={patients}
            columns={patientColumns}
            autoHeight
            initialState={{pagination: {pageSize: 25}}}
            rowsPerPageOptions={[25, 50]}
            experimentalFeatures={{newEditingApi: true}}
          />
        )}
        {tab === 'users' && !_.isEmpty(users) && (
          <DataGrid
            rows={users}
            columns={userColumns}
            autoHeight
            initialState={{pagination: {pageSize: 25}}}
            rowsPerPageOptions={[25, 50]}
            experimentalFeatures={{newEditingApi: true}}
          />
        )}
      </Container>
    </>
  )
}
export default SearchPage
